import type { DropdownOption } from "@bloomberg/media-ui-dropdown";
import type { QueryURL } from "~types/types";
import type { PortfolioForm, PortfolioChartDateRange } from "~types/portfolio";

export const DEFAULT_NATIVE_WIDTH = 1200;

export const ONE_SECOND_IN_MILLISECONDS = 1000;
export const HALF_SECOND_IN_MILLISECONDS = ONE_SECOND_IN_MILLISECONDS / 2;
export const THREE_SECONDS = ONE_SECOND_IN_MILLISECONDS * 3;
export const FOUR_SECONDS = ONE_SECOND_IN_MILLISECONDS * 4;
export const ONE_SECOND = 1;
export const ONE_MINUTE = ONE_SECOND * 60;
export const FIVE_MINUTES = ONE_MINUTE * 5;
export const ONE_HOUR = ONE_MINUTE * 60;
export const ONE_DAY = ONE_HOUR * 24;
export const ONE_WEEK = ONE_DAY * 7;
export const ONE_MONTH = ONE_DAY * 30;
export const ONE_YEAR = ONE_DAY * 365;
export const ONE_MINUTE_IN_MILLISECONDS = ONE_SECOND_IN_MILLISECONDS * 60;
export const ON_HOUR_IN_MILLISECONDS = ONE_MINUTE_IN_MILLISECONDS * 60;

export const COMMON_PORTFOLIO_FORM_VALUES: Pick<
  PortfolioForm,
  "id" | "currency" | "isDefault"
> = {
  id: -1,
  currency: "USD",
  isDefault: false,
};

export const WC_FETCH_BATCH_SIZE = 50;

export const DEFAULT_QUOTE_PAGE_TAB = "summary";

export const ALLOWED_QUOTE_PAGE_TABS: string[] = [
  "financials",
  "info",
  "members",
  "news",
  "summary",
];

export const QUOTE_MEMBERS_FIELDS: { [key: string]: string } = {
  en: "DS520,PR005,PR051,PR052,PR013,PR948,UTIME",
  ja: "DS242,PR005,PR051,PR052,PR013,PR948,UTIME",
};

export const SECTION_FRONT_REFRESH_INTERVAL_IN_MS =
  ONE_MINUTE_IN_MILLISECONDS * 5;

export const HIGH_PRECISION_SECURITY_TYPES = new Set([
  "CUR",
  "CEF",
  "CEFH",
  "MMF",
  "OEF",
  "ETP",
]);

export const SECURITY_TYPES_TO_NAMES: Record<string, string> = {
  CEF: "closed_end_fund",
  CEFH: "hedge_fund",
  COM: "commodity",
  CUR: "currency",
  EQT: "common_stock",
  ETP: "etf",
  GOV: "government_bond",
  IND: "index",
  INDC: "premium",
  INDF: "futures",
  INDN: "rate",
  INDS: "index",
  MMF: "money_market_fund",
  OEF: "open_end_fund",
  OEFH: "premium",
};

export const SECURITY_TYPE_TO_RESOURCE_TYPE: Record<string, string> = {
  CEF: "Fund",
  CEFH: "Fund",
  COM: "Commodity",
  CUR: "Currency",
  EQT: "Company",
  ETP: "Fund",
  GOV: "Bond",
  IND: "Index",
  INDC: "Index",
  INDF: "Index",
  INDN: "Index",
  INDS: "Index",
  MMF: "Fund",
  OEF: "Fund",
  OEFH: "Fund",
};

export const SECURITY_TYPE_TO_ASSET_TYPE: Record<string, string> = {
  CEF: "Fund",
  CEFH: "Fund",
  COM: "Commodity",
  CUR: "Currency",
  EQT: "Stock",
  ETP: "Fund",
  GOV: "Bond",
  IND: "Index",
  INDC: "Index",
  INDF: "Index",
  INDN: "Index",
  INDS: "Index",
  MMF: "Fund",
  OEF: "Fund",
  OEFH: "Fund",
};

// to match WC calcrt types https://bbgithub.dev.bloomberg.com/wss/wardenclyffe/blob/main/src/store/data/security/calcrts/index.js
export const WARDENCLYFFE_SECURITY_TYPES = new Set([
  "CEF",
  "CEFH",
  "COM",
  "CUR",
  "EQT",
  "ETP",
  "GOV",
  "IND",
  "INDF",
  "INDN",
  "MMF",
  "OEF",
]);

export const POPULAR_TICKERS: Record<string, QueryURL> = {
  "SPX:IND": {
    short: { "contentTags.Index": "SPX:IND" },
    mid: { "contentTags.Index": "SPX:IND", "minDirectScore.Index": 1 },
    long: { "contentTags.Index": "SPX:IND", "minDirectScore.Index": 3 },
  },
  "GT10:GOV": {
    short: { query: "us+treasury" },
    mid: { "query.headline": "us+treasury" },
    long: { "query.headline": "us+treasury" },
  },
  "EURUSD:CUR": {
    short: { "tags.terminalTickers": ["%EUR", "%USD"] },
    mid: { "tags.terminalTickers": ["%EUR", "%USD"] },
    long: { "tags.terminalTickers": ["%EUR", "%USD"] },
  },
  "USDEUR:CUR": {
    short: { "tags.terminalTickers": ["%EUR", "%USD"] },
    mid: { "tags.terminalTickers": ["%EUR", "%USD"] },
    long: { "tags.terminalTickers": ["%EUR", "%USD"] },
  },
  "GBPUSD:CUR": {
    short: { "tags.terminalTickers": ["%GBP", "%USD"] },
    mid: { "tags.terminalTickers": ["%GBP", "%USD"] },
    long: { "tags.terminalTickers": ["%GBP", "%USD"] },
  },
  "USDGBP:CUR": {
    short: { "tags.terminalTickers": ["%GBP", "%USD"] },
    mid: { "tags.terminalTickers": ["%GBP", "%USD"] },
    long: { "tags.terminalTickers": ["%GBP", "%USD"] },
  },
};

export const DEFAULT_BENCHMARK_COMPARISONS = [
  "B500:IND",
  "SPX:IND",
  "CCMP:IND",
];

export const BENCHMARK_CHART_TIME_RANGES = [
  "1M",
  "6M",
  "YTD",
  "1Y",
  "5Y",
] as const satisfies readonly PortfolioChartDateRange[];

export const BFIX_LOCATIONS: Array<DropdownOption> = [
  {
    value: "09:30|Asia/Shanghai",
    timezone: "Asia/Shanghai",
    time: "09:30",
    displayValue: "Beijing 9:30 AM",
  },
  {
    value: "11:00|Asia/Singapore",
    timezone: "Asia/Singapore",
    time: "11:00",
    displayValue: "Singapore / Kuala Lumpur 11:00 AM",
  },
  {
    value: "11:30|Asia/Manila",
    timezone: "Asia/Manila",
    time: "11:30",
    displayValue: "Manila 11:30 AM",
  },
  {
    value: "11:00|Asia/Jakarta",
    timezone: "Asia/Jakarta",
    time: "11:00",
    displayValue: "Jakarta / Bangkok 11:00 AM",
  },
  {
    value: "14:00|Asia/Seoul",
    timezone: "Asia/Seoul",
    time: "14:00",
    displayValue: "Seoul 2:00 PM",
  },
  {
    value: "15:00|Asia/Tokyo",
    timezone: "Asia/Tokyo",
    time: "15:00",
    displayValue: "Tokyo 3:00 PM",
  },
  {
    value: "12:00|Asia/Kolkata",
    timezone: "Asia/Kolkata",
    time: "12:00",
    displayValue: "Mumbai 12:00 PM",
  },
  {
    value: "16:00|Europe/London",
    timezone: "Europe/London",
    time: "16:00",
    displayValue: "London 4:00 PM",
  },
  {
    value: "14:00|Europe/Berlin",
    timezone: "Europe/Berlin",
    time: "14:00",
    displayValue: "Frankfurt 2:00 PM",
  },
  {
    value: "12:30|America/New_York",
    timezone: "America/New_York",
    time: "12:30",
    displayValue: "New York 12:30 PM",
  },
  {
    value: "17:00|America/New_York",
    timezone: "America/New_York",
    time: "17:00",
    displayValue: "New York 5:00 PM",
  },
  {
    value: "12:00|America/Toronto",
    timezone: "America/Toronto",
    time: "12:00",
    displayValue: "Ottawa 12:00 PM",
  },
];
